import clsx from 'clsx';
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDays, formatShortTime, relativeTime } from '../lib/format';
import { FormPage } from '../components/FormPage';
import { overrunPenalised, type BreakRequestRef } from '../components/ShiftControl';
import {
  Badge,
  Button,
  Card,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  LoadingBlock,
  Pagination,
  Select,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
  type BadgeTone,
} from '../components/ui';

/**
 * Break requests (operator, 2026-10-18: "no one can take this without admin
 * approval … the request send to admin to approve or reject"): a break type
 * that needs approval is asked for, and whoever holds `attendance.breaks.approve`
 * decides here — and, for that type's requests, the people picked on it
 * (2026-10-19). Approving starts the break at once; rejecting asks for the
 * reason the employee reads.
 */

interface BreakRequestRow extends BreakRequestRef {
  user: { id: string; fullName: string; fullNameAr: string | null; employeeCode: string | null };
  department: { id: string; name: string; nameAr: string | null } | null;
  session: { id: string; startedAt: string; endedAt: string | null };
  /** Approve or reject it: while it waits, and never one's own. */
  can: { decide: boolean };
}

const STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;

/** A request's state at a glance — the same tones here and on My shift. */
export const BREAK_REQUEST_TONE: Record<BreakRequestRef['status'], BadgeTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

/**
 * What a break of this type allows: its limit, what it costs past it, how many times a day
 * it may be taken, whether it counts as work.
 */
function BreakTerms({ type }: { type: BreakRequestRef['breakType'] }) {
  const { t, locale } = useI18n();
  const parts = [
    type.maxMinutes ? `${type.maxMinutes} ${t('common.minutesShort')}` : t('settings.noLimit'),
    ...(overrunPenalised(type) ? [t('breakRequest.penaltyPast', { days: formatDays(type.penaltyDays, locale) })] : []),
    ...(type.maxPerDay ? [t('breakRequest.perDay', { n: type.maxPerDay })] : []),
    t(type.countsAsWork ? 'breakRequest.countsAsWork' : 'breakRequest.offWorked'),
  ];
  return <span className="block text-caption text-muted">{parts.join(' · ')}</span>;
}

/** Attendance › Break requests: waiting ones first, oldest first; any status on request. */
export function BreakRequestsBoard({ focus }: { focus?: string | null }) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Opened from a notification, the request may be decided already: then every status shows.
  const [status, setStatus] = useState<string>(focus ? '' : 'PENDING');
  const [page, setPage] = useState(1);
  const intoView = useCallback((el: HTMLElement | null) => el?.scrollIntoView({ block: 'center' }), []);

  const list = useQuery({
    queryKey: ['attendance', 'break-requests', 'list', status, page],
    queryFn: () =>
      api.get<Paginated<BreakRequestRow>>('/attendance/break-requests', { status: status || undefined, page, pageSize: 25 }),
    // New ones arrive while the tab is open.
    refetchInterval: 15_000,
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['attendance'] });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/attendance/break-requests/${id}/approve`, {}),
    onSuccess: () => {
      toast.success(t('breakRequest.approvedToast'));
      refresh();
    },
    onError: (e: Error & { code?: string }) => {
      // They took that break as many times today as it allows since asking: reject it instead.
      toast.error(e.code === 'BREAK_DAILY_LIMIT' ? t('breakRequest.dailyLimit') : e.message);
      // Decided or withdrawn elsewhere a moment ago: show where it stands now.
      refresh();
    },
  });

  return (
    <div className="space-y-4">
      <Card padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            aria-label={t('common.status')}
          >
            <option value="">
              {t('common.status')} — {t('common.all')}
            </option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {enumLabel(s, locale)}
              </option>
            ))}
          </Select>
        </div>
        <p className="border-t hairline px-4 py-2.5 text-caption text-muted">{t('breakRequest.boardNote')}</p>
      </Card>

      <Card padded={false}>
        {list.isLoading ? (
          <LoadingBlock />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState title={status === 'PENDING' ? t('breakRequest.noneWaiting') : undefined} hint="" />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.employee')}</Th>
                  <Th>{t('breakRequest.break')}</Th>
                  <Th>{t('breakRequest.asked')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((r) => (
                  <Tr
                    key={r.id}
                    className={clsx(r.id === focus && 'bg-primary/8 shadow-[inset_2px_0_0_0_#C7102E] rtl:shadow-[inset_-2px_0_0_0_#C7102E]')}
                  >
                    <Td>
                      <span ref={r.id === focus ? intoView : undefined} className="block" aria-current={r.id === focus ? 'true' : undefined}>
                        {localized(r.user)}
                      </span>
                      {r.department && <span className="text-caption text-faint">{localized(r.department)}</span>}
                    </Td>
                    <Td>
                      <span className="block">{localized(r.breakType)}</span>
                      <BreakTerms type={r.breakType} />
                    </Td>
                    <Td className="whitespace-nowrap">
                      <span className="block tabular">{formatShortTime(r.requestedAt, locale)}</span>
                      <span className="text-caption text-muted">{relativeTime(r.requestedAt, locale)}</span>
                    </Td>
                    <Td className="max-w-xs">
                      <Badge tone={BREAK_REQUEST_TONE[r.status]}>{enumLabel(r.status, locale)}</Badge>
                      {r.status === 'CANCELLED' && r.cancelReason ? (
                        <span className="mt-0.5 block text-caption text-muted">
                          {t(`breakRequest.cancel.${r.cancelReason}` as TranslationKey)}
                        </span>
                      ) : (
                        r.decidedBy && (
                          <span className="mt-0.5 block text-caption text-muted">
                            {t('breakRequest.decidedBy', { name: localized(r.decidedBy) })}
                            {r.decidedAt && <span className="tabular"> · {formatShortTime(r.decidedAt, locale)}</span>}
                          </span>
                        )
                      )}
                      {r.decisionNote && (
                        <span dir="auto" className="block truncate text-caption text-faint" title={r.decisionNote}>
                          “{r.decisionNote}”
                        </span>
                      )}
                    </Td>
                    <Td align="end">
                      {r.can.decide && (
                        <span className="inline-flex flex-wrap justify-end gap-1">
                          <Button
                            size="sm"
                            variant="primary"
                            icon={<Check className="h-3.5 w-3.5" />}
                            loading={approve.isPending && approve.variables === r.id}
                            disabled={approve.isPending}
                            onClick={() => approve.mutate(r.id)}
                          >
                            {t('common.approve')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<X className="h-3.5 w-3.5" />}
                            disabled={approve.isPending}
                            onClick={() => navigate(`/attendance/break-requests/${r.id}/reject`)}
                          >
                            {t('common.reject')}
                          </Button>
                        </span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={list.data.page} pageCount={list.data.pageCount} total={list.data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}

/** Route: `/attendance/break-requests/:id/reject` — the employee reads the reason, so there must be one. */
export function RejectBreakRequestPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const request = useQuery({
    queryKey: ['attendance', 'break-requests', 'one', id],
    queryFn: () => api.get<BreakRequestRow>(`/attendance/break-requests/${id}`),
  });

  const reject = useMutation({
    mutationFn: () => api.post(`/attendance/break-requests/${id}/reject`, { note: note.trim() }),
    onSuccess: () => {
      toast.success(t('breakRequest.rejectedToast'));
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
      // Back to the list it came from; opened on its own, to the requests.
      if (location.key !== 'default') navigate(-1);
      else navigate('/attendance?tab=requests', { replace: true });
    },
    onError: (e: Error) => {
      toast.error(e.message);
      void request.refetch();
    },
  });

  const r = request.data;
  const closed = !!r && !r.can.decide;

  return (
    <FormPage
      title={t('breakRequest.rejectTitle')}
      backLabel={t('common.back')}
      submitLabel={t('common.reject')}
      destructive
      submitting={reject.isPending}
      disabled={!note.trim() || closed}
      loading={request.isLoading}
      error={request.error}
      onRetry={() => void request.refetch()}
      onSubmit={() => reject.mutate()}
      summary={
        r && (
          <dl>
            <Detail label={t('common.employee')}>{localized(r.user)}</Detail>
            <Detail label={t('breakRequest.break')}>{localized(r.breakType)}</Detail>
            <Detail label={t('breakRequest.asked')}>
              <span className="tabular">{formatShortTime(r.requestedAt, locale)}</span> · {relativeTime(r.requestedAt, locale)}
            </Detail>
            <Detail label={t('common.status')}>
              <Badge tone={BREAK_REQUEST_TONE[r.status]}>{enumLabel(r.status, locale)}</Badge>
            </Detail>
          </dl>
        )
      }
    >
      {closed && <p className="mb-4 text-body-sm text-warning-fg dark:text-warning-dark">{t('breakRequest.noLongerWaiting')}</p>}
      <Field label={t('breakRequest.rejectReason')} required>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} autoFocus disabled={closed} />
      </Field>
    </FormPage>
  );
}
